import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import authService, { User, LoginCredentials } from '../services/auth.service';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    error: string | null;
    login: (credentials: LoginCredentials) => Promise<void>;
    logout: () => Promise<void>;
    refreshUser: () => Promise<void>;
    hasRole: (role: User['role']) => boolean;
    hasPermission: (resource: string, action: string) => boolean;
    clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Initialize auth state
    useEffect(() => {
        const initAuth = async () => {
            try {
                if (authService.isAuthenticated()) {
                    const currentUser = await authService.getCurrentUser();
                    setUser(currentUser);
                    authService.setUser(currentUser);
                }
            } catch (error) {
                console.error('Failed to initialize auth:', error);
                authService.clearAuth();
            } finally {
                setLoading(false);
            }
        };

        initAuth();
    }, []);

    const login = useCallback(async (credentials: LoginCredentials) => {
        try {
            setError(null);
            setLoading(true);
            const { user } = await authService.login(credentials);
            setUser(user);
        } catch (error: any) {
            const message = error.response?.data?.error || 'Login failed. Please try again.';
            setError(message);
            throw error;
        } finally {
            setLoading(false);
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            setLoading(true);
            await authService.logout();
            setUser(null);
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const refreshUser = useCallback(async () => {
        try {
            const currentUser = await authService.getCurrentUser();
            setUser(currentUser);
            authService.setUser(currentUser);
        } catch (error) {
            console.error('Failed to refresh user:', error);
        }
    }, []);

    const hasRole = useCallback(
        (role: User['role']) => {
            return authService.hasRole(role);
        },
        [user]
    );

    const hasPermission = useCallback(
        (resource: string, action: string) => {
            return authService.hasPermission(resource, action);
        },
        [user]
    );

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const value: AuthContextType = {
        user,
        loading,
        error,
        login,
        logout,
        refreshUser,
        hasRole,
        hasPermission,
        clearError
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};